import React from 'react';
import { motion } from 'framer-motion';
import Press from './Press';
import Testimonials from './Testimonials';
import DestinationsMarquee from './DestinationsMarquee';
import CTA from './CTA';

const features = [
  {
    icon: '🏛️',
    title: 'ELEGANT VENUES',
    desc: 'Sophisticated banquet halls in Surat & Mumbai designed for memorable celebrations and corporate events.',
    img: '/img/i21.jpg',
    points: [
      'Spacious halls with customizable layouts',
      'Premium interior design',
      'State-of-the-art facilities',
      'Dedicated parking',
    ],
  },
  {
    icon: '🍽️',
    title: 'GOURMET CATERING',
    desc: 'Exquisite culinary experiences with customized menus crafted by our expert chefs.',
    img: '/img/i39.jpg',
    points: [
      'Multi-cuisine options',
      'Live cooking stations',
      'Premium ingredients',
      'Dietary accommodations',
    ],
  },
  {
    icon: '📋',
    title: 'EVENT COORDINATION',
    desc: 'Professional event management ensuring seamless execution from planning to completion.',
    img: '/img/i20.jpg',
    points: [
      'Dedicated event managers',
      'Timeline coordination',
      'Vendor management',
      'Day-of supervision',
    ],
  },
  {
    icon: '✨',
    title: 'PREMIUM AMENITIES',
    desc: 'Modern facilities and luxury amenities to enhance your event experience.',
    img: '/img/i33.jpg',
    points: [
      'Audio-visual equipment',
      'Climate control',
      'Bridal suites',
      'VIP lounges',
    ],
  },
];

const venues = [
  {
    abbr: 'S',
    name: 'Heritage Elegance',
    caption: 'Traditional charm meets modern luxury',
    img: '/img/i12.jpg',
  },
  {
    abbr: 'M',
    name: 'Metropolitan Grandeur',
    caption: 'Cosmopolitan sophistication in the heart of the city',
    img: '/img/i31.jpg',
  },
];

const Banquets: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#faf8f6]">
      {/* Hero - Image Banner */}
      <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden pt-20 sm:pt-0">
        <img
          src="/img/i10.jpg"
          alt="Elegant Banquet Experiences"
          className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-[15s] ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="uppercase tracking-[0.3em] sm:tracking-[0.5em] text-[10px] sm:text-xs text-white/80 mb-6 sm:mb-8"
            >
              Luxury Event Experiences
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-tight mb-6 sm:mb-8">
               ELEGANT BANQUET EXPERIENCES
             </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-sm sm:text-base md:text-lg text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-12"
            >
              From intimate corporate gatherings to grand celebrations, we create sophisticated banquet experiences that leave lasting impressions. Every detail is meticulously crafted to reflect your vision and exceed expectations.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex items-center justify-center"
            >
              <a href="/#contact" className="group relative px-8 sm:px-12 py-4 sm:py-6 border-2 border-white/80 text-white text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.4em] uppercase bg-transparent hover:bg-white hover:text-black transition-all duration-500 rounded-sm backdrop-blur-sm">
                <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-4 text-white group-hover:text-black transition-colors duration-300">
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                  <span className="block w-3 h-3 rounded-full border-2 border-white/70 group-hover:border-black/70 transition-colors" />
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                </span>
                PLAN YOUR EVENT
                <div className="absolute inset-0 bg-white/5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </a>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute bottom-10 left-10 opacity-30">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        </div>
        <div className="absolute top-20 right-20 opacity-20">
          <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </section>



      {/* Features (Impact) */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-xs uppercase tracking-[0.4em] text-gray-500 mb-4">
              EXCEPTIONAL FEATURES
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
              PREMIUM BANQUET AMENITIES
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Every detail is designed to create unforgettable experiences for your guests
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "LUXURY DINING",
                description: "Exquisite multi-course menus crafted by award-winning chefs using the finest ingredients",
                image: "/img/i11.jpg"
              },
              {
                title: "ELEGANT VENUES",
                description: "Sophisticated spaces with premium décor and state-of-the-art lighting systems",
                image: "/img/i12.jpg"
              },
              {
                title: "PREMIUM SERVICE",
                description: "Dedicated event coordinators ensuring flawless execution from start to finish",
                image: "/img/i13.jpg"
              },
              {
                title: "CUSTOM DÉCOR",
                description: "Personalized styling and floral arrangements to match your vision perfectly",
                image: "/img/i14.jpg"
              },
              {
                title: "ENTERTAINMENT",
                description: "Professional sound systems and entertainment options for memorable celebrations",
                image: "/img/i15.jpg"
              },
              {
                title: "PHOTOGRAPHY",
                description: "Stunning backdrops and lighting designed for capturing perfect moments",
                image: "/img/i16.jpg"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden bg-white rounded-lg border border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-500"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Decorative corner element */}
                  <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                
                <div className="p-8">
                  <h3 className="text-lg font-serif text-gray-900 mb-4 tracking-wide">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {feature.description}
                  </p>
                  
                  {/* Hover indicator */}
                  <div className="mt-6 flex items-center text-xs uppercase tracking-[0.3em] text-gray-400 group-hover:text-gray-600 transition-colors duration-300">
                    <span>LEARN MORE</span>
                    <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
                
                {/* Subtle border animation */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-gray-100 transition-colors duration-500 rounded-lg" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Venues */}
      <section className="py-24 bg-gradient-to-b from-[#395958] to-[#2a4342] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-xs uppercase tracking-[0.4em] text-[#F1D291] mb-4">
              PREMIUM LOCATIONS
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">
              EXQUISITE VENUES
            </h2>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              Discover our collection of stunning venues, each designed to create the perfect atmosphere for your celebration
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {[
              {
                title: "GRAND BALLROOM",
                location: "Surat Heritage",
                capacity: "300-500 Guests",
                description: "Our flagship venue featuring soaring ceilings, crystal chandeliers, and panoramic city views. Perfect for grand celebrations and corporate galas.",
                features: ["Crystal Chandeliers", "Panoramic Views", "Premium Sound System", "Dedicated Bridal Suite"],
                image: "/img/i17.jpg"
              },
              {
                title: "GARDEN PAVILION",
                location: "Mumbai Elegance",
                capacity: "150-250 Guests",
                description: "An enchanting outdoor venue surrounded by manicured gardens and water features. Ideal for intimate celebrations under the stars.",
                features: ["Garden Setting", "Water Features", "Ambient Lighting", "Climate Control"],
                image: "/img/i18.jpg"
              },
              {
                title: "ROYAL SUITE",
                location: "Surat Premium",
                capacity: "100-200 Guests",
                description: "Intimate luxury venue with rich décor and personalized service. Perfect for exclusive gatherings and milestone celebrations.",
                features: ["Luxury Interiors", "Private Entrance", "Personalized Service", "Custom Décor"],
                image: "/img/i19.jpg"
              },
              {
                title: "TERRACE LOUNGE",
                location: "Mumbai Skyline",
                capacity: "80-150 Guests",
                description: "Modern rooftop venue with stunning city skyline views. Ideal for cocktail receptions and contemporary celebrations.",
                features: ["Skyline Views", "Modern Design", "Cocktail Bar", "Open Air Setting"],
                image: "/img/i20.jpg"
              }
            ].map((venue, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden bg-white rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={venue.image} 
                    alt={venue.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  
                  {/* Venue info overlay */}
                  <div className="absolute bottom-6 left-6 text-white">
                    <div className="text-xs uppercase tracking-[0.3em] text-white/80 mb-2">
                      {venue.location}
                    </div>
                    <h3 className="text-2xl font-serif mb-2">
                      {venue.title}
                    </h3>
                    <div className="text-sm text-white/90">
                      {venue.capacity}
                    </div>
                  </div>
                  
                  {/* Decorative corner element */}
                  <div className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                
                <div className="p-8">
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {venue.description}
                  </p>
                  
                  <div className="space-y-3 mb-8">
                    {venue.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                  
                  {/* Action button */}
                  <div className="flex items-center text-xs uppercase tracking-[0.3em] text-gray-400 group-hover:text-gray-600 transition-colors duration-300">
                    <span>VIEW DETAILS</span>
                    <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
                
                {/* Subtle border animation */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-gray-100 transition-colors duration-500 rounded-lg" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      <Testimonials />
      <Press />
      <DestinationsMarquee />
      <CTA />
    </div>
  );
};

export default Banquets;